// menuItemModel.js

const REDIR_BASE = "https://mydata.tablemate.net/redir.php?url=";

// Helper: figure out the tenant root (multi-tenant strict)
// MUST be provided via dto.server_url / dto.server / dto.raw.server_url / dto.raw.server
function resolveTenantRoot(dto) {
  const raw = dto?.raw ?? {};

  const fromDto =
    dto?.server_url ||
    dto?.server ||
    raw.server_url ||
    raw.server ||
    null;

  if (!fromDto) {
    throw new Error(
      "menuItemModel: Missing tenant server URL (server_url/server) in DTO. " +
      "Multi-tenant mode requires each menu item to carry its server_url/server."
    );
  }

  // strip trailing slashes
  return fromDto.replace(/\/+$/, "");
}

// Helper: build proxied image URL via redir.php
function buildImagePreviewUrl(filename, dto) {
  if (!filename) return "";

  const root = resolveTenantRoot(dto); // e.g. https://tenant1.tablemate.net/tablemate
  const upstream = `${root}/admin/images/${filename}`;

  // Important: encode the full upstream URL
  return `${REDIR_BASE}${encodeURIComponent(upstream)}`;
}

/** Parse API item (with .raw) into Edit Menu Item model */
export function parseMenuItemToEditModel(dto) {
  console.log("menu item: " + JSON.stringify(dto));

  // your API often sends { id, name, price, image, allergens, raw:{...} }
  const r = dto?.raw ?? dto ?? {};

  // id / codes
  const id      = String(r.id ?? dto.id ?? "");
  const code    = String(r.code ?? dto.code ?? "");
  const subcode = String(r.subcode ?? dto.subcode ?? "");

  // name
  const name    = String(r.item ?? dto.name ?? "");

  // price → price1/price2 split
  const pNum   = Number(r.price ?? dto.price ?? 0);
  const safe   = isFinite(pNum) ? pNum : 0;
  const price1 = String(Math.trunc(safe));
  const price2 = String(Math.round((safe * 100) % 100)).padStart(2, "0");

  // image/url handling
  const urlRaw = r.url ?? dto.image ?? "";
  // strip /admin[/]/images/ prefix to get just the filename, but keep original for preview
  const filename =
    typeof urlRaw === "string"
      ? urlRaw
          .replace(/^\/?admin\/?\/?images\/?/i, "")
          .replace(/^\/+/, "")
      : "";

  // If there *is* a filename, we REQUIRE a tenant server URL;
  // resolveTenantRoot() will throw if missing.
  const imagePreview = filename
    ? buildImagePreviewUrl(filename, dto)
    : "";

  // other fields
  const description = String(r.description ?? "");
  const quantity    = Number(r.quantity ?? 0);
  const warning     = Number(r.warning ?? 0);
  const rating      = Number(r.rating ?? 3);
  const visible     = String(r.visibility ?? "1") === "1";

  // text blobs (kept as-is; you already store like "Standard<>0.00--…")
  const sizes          = String(r.portions ?? r.sizes ?? "");
  const sides          = String(r.sides ?? "");
  const specifications = String(r.specifications ?? "");

  return {
    // read-only in UI
    id,
    code,
    subcode,

    // editable fields (mirror your modal state)
    name,
    price1,
    price2,
    description,
    quantity,
    warning: warning ?? "",
    rating,
    visible,                 // boolean
    url: filename || urlRaw || "", // we keep filename if pathy, else raw
    imagePreview,            // convenience for the modal

    sizes,
    sides,
    specifications,
  };
}

/** Build payload for updateMenuItem() from the edit model */
export function buildUpdatePayloadFromModel(model) {
  const price = Number(
    `${String(model.price1 || "0")}.${String(model.price2 || "00").padStart(
      2,
      "0"
    )}`
  );

  return {
    id:        model.id,
    subcode:   model.subcode, // read-only echo
    item:      model.name,
    price1:    String(model.price1 ?? "0"),
    price2:    String((model.price2 ?? "0")).padStart(2, "0"),
    // optional: also send combined price if your PHP accepts it
    price,

    description: model.description ?? "",
    visibility:  model.visible ? 1 : 0,
    url:         model.url ?? "", // filename or absolute URL
    rating:      Number(model.rating ?? 3),
    quantity:    Number(model.quantity ?? 0),

    sizes:          model.sizes ?? "", // → portions
    sides:          model.sides ?? "",
    specifications: model.specifications ?? "",
    allergen_ids: Array.isArray(model.allergen_ids)
      ? model.allergen_ids.join(",")
      : model.allergen_ids ?? "",
  };
}
